import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { pagosProveedorApi } from '../api/pagosProveedor'
import Boton from '../components/Boton'
import ModalEnviarLegajo from '../components/ModalEnviarLegajo'
import { fmtFecha, fmtFechaHora, fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius } from '../design/tokens'

// Detalle de un pago a proveedor: el legajo tal como sale, los envíos y el
// atajo al próximo paso (enviar / reenviar). Martin, 26 oct 2026.

const CANAL = { email_lgr: 'Mail de LGR', email_local: 'Mi programa de correo (preparado)', whatsapp: 'WhatsApp (abierto)' }
// Lo que la API de MP no informa (28/10: ninguna de las 5 a bodegas): su comprobante lo adjunta
// el pagador al enviar, igual que el del banco (Martin, 28 oct 2026).
const MP_ADJUNTAR = 'Adjuntá el comprobante de Mercado Pago al enviar el legajo, como el del banco.'
const MP_TEXTO = {
  encontrada: 'Transferencia de Mercado Pago encontrada: su comprobante va en el legajo.',
  sin_coincidencia: `Mercado Pago no informa esta transferencia por su API. ${MP_ADJUNTAR}`,
  ambigua: `Hay más de una transferencia de ese día por ese importe en Mercado Pago: no se adjunta ninguna sola. ${MP_ADJUNTAR}`,
  no_disponible: `Mercado Pago no respondió; se vuelve a buscar al reabrir el pago. Si no aparece: ${MP_ADJUNTAR.charAt(0).toLowerCase()}${MP_ADJUNTAR.slice(1)}`,
}

export default function PagoProveedorDetallePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [det, setDet] = useState(null)
  const [html, setHtml] = useState(null)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(false)
  const [bajando, setBajando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const { data } = await pagosProveedorApi.ver(id)
      setDet(data)
      if (data.legajo) {
        const r = await pagosProveedorApi.legajoHtml(id)
        setHtml(r.data)
      }
    } catch { setError('No se pudo cargar el pago.') }
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  if (error) return <div style={st.err}>{error}</div>
  if (!det) return <div style={st.meta}>Cargando…</div>

  const l = det.legajo
  const bajarPdf = async () => {
    setBajando(true)
    try { await pagosProveedorApi.descargarPdf(id, `Legajo-OP-${det.numero}.pdf`) } finally { setBajando(false) }
  }

  return (
    <div>
      <div style={st.toolbar}>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        <div style={{ display: 'flex', gap: space.md }}>
          <Boton variante="secondary" disabled={!l || bajando} onClick={bajarPdf}>{bajando ? 'Generando…' : '⬇ PDF'}</Boton>
          <Boton variante="send" disabled={!l} onClick={() => setModal(true)}>{det.envios.length ? '↻ Reenviar legajo' : '→ Enviar legajo'}</Boton>
        </div>
      </div>

      <div style={st.cab}>
        <div>
          <div style={st.eb}>Orden de pago</div>
          <div style={st.num}><span style={{ userSelect: 'all', whiteSpace: 'nowrap' }}>{det.numero}</span></div>
          {l && <div style={st.meta}>{l.pago.fecha} {l.pago.hora} · {l.pago.operador} · nº Presea <span style={st.mono}>{det.numero_presea}</span></div>}
        </div>
        {l && (
          <div style={{ textAlign: 'right' }}>
            <div style={st.prov}>{l.proveedor.nombre}</div>
            <div style={st.meta}>Prov. <span style={st.mono}>{l.proveedor.codigo}</span>{l.proveedor.cuit && <> · CUIT <span style={st.mono}>{l.proveedor.cuit}</span></>}</div>
            <div style={st.total}>$ {fmtMonto(l.pago.total)}</div>
          </div>
        )}
      </div>

      {det.legajo_error && <div style={st.err}>{det.legajo_error}</div>}
      {det.mp_estado && det.mp_estado !== 'no_aplica' && (
        <div style={det.mp_estado === 'encontrada' ? st.ok : st.warn}>{MP_TEXTO[det.mp_estado]}</div>
      )}

      <div style={st.grid}>
        <div style={st.card}>
          <div style={st.cardTit}>Legajo</div>
          {html
            ? <iframe title="Legajo" srcDoc={html} style={st.frame} />
            : <div style={st.meta}>{l ? 'Cargando la vista previa…' : 'Sin legajo.'}</div>}
        </div>

        <div style={{ display: 'grid', gap: space.lg, alignContent: 'start' }}>
          <div style={st.card}>
            <div style={st.cardTit}>Contacto para el envío</div>
            <div style={st.kv}><span>Email</span><span>{det.contacto.email || <span style={{ color: colors.errStrong }}>sin email</span>}</span></div>
            <div style={st.kv}><span>WhatsApp</span><span style={st.mono}>{det.contacto.telefono || '—'}</span></div>
            <div style={st.meta}>
              {det.contacto.email_origen === 'ultimo' || det.contacto.telefono_origen === 'ultimo'
                ? `Último usado${det.contacto.cambiado_por ? ` por ${det.contacto.cambiado_por}` : ''}${det.contacto.cambiado_at ? ` el ${fmtFecha(det.contacto.cambiado_at)}` : ''}.`
                : 'Del maestro de proveedores de Presea.'}
            </div>
          </div>

          <div style={st.card}>
            <div style={st.cardTit}>Envíos</div>
            {det.envios.length === 0 ? <div style={st.meta}>Todavía no se mandó.</div> : det.envios.map(e => (
              <div key={e.id} style={st.envio}>
                <div><b style={{ fontWeight: 600 }}>{CANAL[e.canal] ?? e.canal}</b> <span style={st.meta}>· {e.usuario ?? '—'}</span></div>
                <div style={st.meta}><span style={st.mono}>{fmtFechaHora(e.enviado_at)}</span> → {e.destinatario}</div>
                {e.adjuntos?.length > 0 && <div style={st.meta}>📎 {e.adjuntos.join(', ')}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && <ModalEnviarLegajo pagoId={det.id} onClose={() => setModal(false)} onEnviado={(_, { cerrar }) => { cargar(); if (cerrar) setModal(false) }} />}
    </div>
  )
}

const st = {
  toolbar: { position: 'sticky', top: 0, zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${space.md}px 0`, background: colors.bgSoft, marginBottom: space.md },
  cab: { display: 'flex', justifyContent: 'space-between', gap: space.xl, flexWrap: 'wrap', padding: space.lg, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, marginBottom: space.md },
  eb: { fontSize: text.meta, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec },
  num: { fontSize: text.display, fontWeight: 600, fontFamily: font.mono },
  prov: { fontSize: text.title, fontWeight: 600, color: colors.text },
  total: { fontSize: 20, fontWeight: 700, fontFamily: font.mono, marginTop: 4 },
  meta: { fontSize: text.sec, color: colors.textSec },
  mono: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: space.lg, alignItems: 'start' },
  card: { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.lg, minWidth: 0 },
  cardTit: { fontSize: text.meta, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec, marginBottom: space.md },
  frame: { width: '100%', height: '80vh', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: '#fff' },
  kv: { display: 'grid', gridTemplateColumns: '80px 1fr', gap: space.sm, fontSize: text.body, marginBottom: 4, wordBreak: 'break-word' },
  envio: { padding: '6px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  err: { padding: '8px 12px', background: colors.errBg, color: colors.err, borderRadius: radius.sm, fontSize: text.sec, marginBottom: space.md },
  warn: { padding: '8px 12px', background: colors.warnBg, color: colors.warn, borderRadius: radius.sm, fontSize: text.sec, marginBottom: space.md },
  ok: { padding: '8px 12px', background: colors.okSoft, color: colors.ok, borderRadius: radius.sm, fontSize: text.sec, marginBottom: space.md },
}
